import React from "react";
import { useApiService } from "./apiService";
import { User, UpdateUserRequest } from "../types/user";
import { CurrentUser, UserAccessItem, UserSession } from "../types/Auth";
import { UserTenantRole } from "../types/UserTenantRole";
import { isGlobalAdmin } from "../types/roles";
import { PermissionMap } from "../types/permissions";

// Cache to prevent duplicate simultaneous requests
let userSessionCache: { promise: Promise<UserSession> | null; timestamp: number } = { promise: null, timestamp: 0 };
let permissionsCache: Map<string, { promise: Promise<PermissionMap> | null; timestamp: number }> = new Map();
const CACHE_TTL = 5000; // 5 seconds to prevent duplicate calls in StrictMode

/// Clears all in-memory session and permission caches.
/// Call this on login/logout to ensure fresh data is fetched.
export const clearSessionCache = (): void => {
  userSessionCache = { promise: null, timestamp: 0 };
  permissionsCache.clear();
};

export const useUserService = () => {
  const { apiCall } = useApiService();

  const getCurrentUser = React.useCallback(async (): Promise<CurrentUser> => {
    const response = await apiCall<CurrentUser>({
      method: "GET",
      endpoint: "/api/AppUsers/me",
      requireAuth: true,
    });
    return response.data;
  }, [apiCall]);

  const getUserAccess = React.useCallback(async (): Promise<UserAccessItem[]> => {
    const response = await apiCall<UserAccessItem[]>({
      method: "GET",
      endpoint: "/api/AppUsers/me/access",
      requireAuth: true,
    });
    return response.data;
  }, [apiCall]);

  const getUserSession = React.useCallback(async (): Promise<UserSession> => {
    const now = Date.now();
    
    // If there's a pending request within the cache TTL, return it
    if (userSessionCache.promise && (now - userSessionCache.timestamp) < CACHE_TTL) {
      return userSessionCache.promise;
    }

    // Create new request
    const promise: Promise<UserSession> = (async () => {
      try {
        // Call both endpoints
        const [user, access] = await Promise.all([
          getCurrentUser(),
          getUserAccess()
        ]);

    // Check if user is global admin
    const globalAdminAccess = access.find(a => isGlobalAdmin(a.role));
    // Special case: temporarily make specific user global admin
  const isGlobalAdminUser = !!globalAdminAccess;

        // Transform access items to tenants
    const tenants: UserTenantRole[] = access
  .filter(a => a.tenant !== null)
  .map(a => ({
    tenantId: a.tenant!.id,
    tenantName: a.tenant!.name,
    role: a.role,
    tenantStatus: a.tenant!.status,
    logoUrl: a.tenant!.logoUrl
  }));

        return {
          user,
          access,
          isGlobalAdmin: isGlobalAdminUser,
          tenants,
        };
      } finally {
        // Clear cache after request completes (success or error)
        setTimeout(() => {
          if (userSessionCache.promise === promise) {
            userSessionCache = { promise: null, timestamp: 0 };
          }
        }, CACHE_TTL);
      }
    })();
    
    userSessionCache = { promise, timestamp: now };
    return promise;
  }, [getCurrentUser, getUserAccess]);

  const getUserPermissions = React.useCallback(async (tenantId?: string | null): Promise<PermissionMap> => {
    const cacheKey = tenantId || 'global';
    const now = Date.now();
    
    // Check cache
    const cached = permissionsCache.get(cacheKey);
    if (cached && cached.promise && (now - cached.timestamp) < CACHE_TTL) {
      return cached.promise;
    }

    const endpoint = tenantId 
      ? `/api/AppUsers/me/permissions?tenantId=${tenantId}`
      : `/api/AppUsers/me/permissions`; // No tenantId = global admin
    
    const promise: Promise<PermissionMap> = (async () => {
      try {
        const response = await apiCall<PermissionMap>({
          method: "GET",
          endpoint,
          requireAuth: true,
        });
        
        return response.data;
      } finally {
        // Clear cache after request completes (success or error)
        setTimeout(() => {
          const currentCache = permissionsCache.get(cacheKey);
          if (currentCache && currentCache.promise === promise) {
            permissionsCache.delete(cacheKey);
          }
        }, CACHE_TTL);
      }
    })();

    permissionsCache.set(cacheKey, { promise, timestamp: now });
    return promise;
  }, [apiCall]);

  const getUserById = React.useCallback(async (userId: string): Promise<User> => {
    const response = await apiCall<User>({
      method: "GET",
      endpoint: `/api/AppUsers/${userId}`,
      requireAuth: true,
    });
    return response.data;
  }, [apiCall]);

  const getAllUsers = React.useCallback(async (): Promise<User[]> => {
    const response = await apiCall<User[]>({
      method: "GET",
      endpoint: "/api/AppUsers",
      requireAuth: true,
    });
    return response.data;
  }, [apiCall]);

  const updateUser = React.useCallback(async (
    userId: string,
    updates: UpdateUserRequest
  ): Promise<User> => {
    const response = await apiCall<User>({
      method: "PUT",
      endpoint: `/api/AppUsers/${userId}`,
      payload: updates,
      requireAuth: true,
    });
    return response.data;
  }, [apiCall]);

  return {
    getCurrentUser,
    getUserAccess,
    getUserSession,
    getUserPermissions,
    getUserById,
    getAllUsers,
    updateUser,
  };
};
